"use client"

import { useCallback, useEffect, useRef } from "react"
import { captureChatError } from "@/lib/sentry-chat"

/**
 * Which client-generated launcher line is being echoed. Mirrors glue's
 * `ECHO_KINDS` (`glue/src/echo-route.ts`) — a **cross-repo contract**, pinned by
 * a test on both sides: glue rejects a kind it doesn't know. The wording travels
 * in `text` (already localized by the widget); `kind` is only the label.
 */
export type EchoKind =
  | "welcome"
  | "answer_failed"
  | "connecting_team"
  | "switch_locale"
  | "out_of_hours_submitted"
  | "slow_reply_submitted"

/** A launcher line waiting to be echoed into Chatwoot. */
export interface PendingEchoLine {
  kind: EchoKind
  text: string
}

interface UseEchoLauncherLinesArgs {
  /** Per-contact widget JWT (`session.source_id`) — glue authorizes with it. */
  authToken: string | null
  /** Null until the visitor's first message creates the conversation. */
  conversationId: number | null
  /** The locale the visitor is seeing the widget in — sent for provenance. */
  locale: string
  /** Lines buffered before a conversation existed (persisted in the session). */
  pendingEchoLines: PendingEchoLine[]
  /** Persist a line to flush once the conversation id is known. */
  bufferEchoLine: (line: PendingEchoLine) => void
  /** Clear the persisted buffer (called optimistically, before flushing). */
  drainEchoLines: () => void
  /**
   * When true, buffered lines are DISCARDED rather than flushed — the
   * conversation glue handed us is a continuation (AHD-10752 stitch), so the
   * pre-conversation greeting would land mid-thread in an existing Chatwoot
   * conversation. Set from the `session` event's `stitched` flag, which is what
   * makes this race-free: the flush is triggered the instant the conversation
   * id arrives (that same event), so the suppression signal arrives with it.
   */
  suppressBufferedFlush: boolean
  /**
   * Records the created Chatwoot message id so the widget suppresses the copy the
   * ActionCable subscription / catch-up poll delivers back. This is the SAME set
   * the bot-answer `chatwoot_echo` flow feeds, so the customer never sees the
   * line twice.
   */
  onEchoed: (chatwootMessageId: number) => void
  /**
   * Reserves the exact text for content-based suppression, synchronously, before
   * the request goes out. Closes the race where Chatwoot broadcasts the outgoing
   * message over the subscription before the POST returns its id — a system-role
   * turn (answer_failed, connecting_team, …) would otherwise be rendered as a
   * duplicate human reply, because the content backstop only matched bot turns.
   */
  onEchoQueued: (text: string) => void
}

/**
 * Mirror a client-generated launcher line the widget just showed the visitor
 * into the Chatwoot conversation as a visible bot message, and record the
 * returned Chatwoot message id so the copy Chatwoot echoes back is dropped (the
 * visitor must never see it twice).
 *
 * Returns an imperative `echo(kind, text)` to call at each `appendTurn` site with
 * the exact localized string the visitor saw.
 *
 * **The pre-conversation greeting.** The Ella greeting fires ~700ms after the
 * panel opens, before the visitor has sent anything — so no Chatwoot conversation
 * exists yet (it is created by the first real message). glue must never create an
 * empty conversation just to hold a greeting, so lines fired before the id is
 * known are BUFFERED (persisted in the chat session, because ChatPanel unmounts
 * on collapse and a ref would forget) and FLUSHED in order once the id arrives.
 *
 * **Ordering.** A buffered line is flushed right after the first user message
 * created the conversation, so in Chatwoot the greeting lands just *after* that
 * first message rather than before it — the conversation cannot exist any
 * earlier. Content is complete and lines keep their relative order; only
 * greeting-vs-first-message differs from what the visitor saw. Correctness of
 * content is preferred over perfect ordering here (see the PR body).
 *
 * Fire-and-forget: a failure is reported to Sentry and not retried, exactly like
 * the email-ask announce — the line the visitor saw must never depend on Chatwoot
 * being reachable.
 */
export function useEchoLauncherLines(
  args: UseEchoLauncherLinesArgs
): (kind: EchoKind, text: string) => void {
  const {
    authToken,
    conversationId,
    pendingEchoLines,
    drainEchoLines,
    suppressBufferedFlush,
  } = args

  // Latest args in a ref so `echo`/`send` stay referentially stable — they're
  // called from effects (the welcome timer) and from stable SSE handlers, and a
  // changing identity would churn those or re-run the welcome.
  const ref = useRef(args)
  ref.current = args

  const send = useCallback(async (line: PendingEchoLine): Promise<void> => {
    const { authToken, conversationId, locale, onEchoed, onEchoQueued } =
      ref.current
    if (!authToken || conversationId === null) return

    // Reserve the content-suppression synchronously (before the first await) so
    // the copy Chatwoot broadcasts is dropped even if it beats this response.
    onEchoQueued(line.text)

    try {
      const res = await fetch("/api/chat/echo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Token": authToken,
        },
        body: JSON.stringify({
          text: line.text,
          locale,
          kind: line.kind,
          conversation_id: conversationId,
        }),
      })
      if (!res.ok) throw new Error(`echo failed (${res.status})`)
      const data = (await res.json()) as { chatwoot_message_id?: number }
      if (typeof data.chatwoot_message_id === "number") {
        onEchoed(data.chatwoot_message_id)
      }
    } catch (err) {
      captureChatError(err, {
        conversation_id: conversationId,
        extra: { form: "echo", kind: line.kind },
      })
    }
  }, [])

  const echo = useCallback(
    (kind: EchoKind, text: string): void => {
      const { authToken, conversationId, bufferEchoLine } = ref.current
      const trimmed = text.trim()
      if (!trimmed) return
      const line: PendingEchoLine = { kind, text: trimmed }
      if (authToken && conversationId !== null) {
        void send(line)
      } else {
        // No conversation yet (the greeting) — buffer it for the flush below.
        bufferEchoLine(line)
      }
    },
    [send]
  )

  // Flush buffered lines once the conversation id (and JWT) are known.
  useEffect(() => {
    if (!authToken || conversationId === null) return
    if (pendingEchoLines.length === 0) return

    const lines = pendingEchoLines
    // Clear the persisted buffer up front so a concurrent remount can't flush the
    // same lines again. The trade (a failed flush is not retried) matches the
    // email-ask announce's "mark before the request".
    drainEchoLines()

    // Stitched conversation (AHD-10752): the id we just learned belongs to an
    // EXISTING thread, so these pre-conversation lines (the greeting) must not be
    // echoed into it mid-thread. Discard them — draining above already cleared
    // the persisted buffer.
    if (suppressBufferedFlush) return

    // Sequential, not Promise.all, so the lines land in Chatwoot in the order the
    // visitor saw them. A reduce-chain rather than an await-in-loop.
    void lines.reduce<Promise<void>>(
      (prev, line) => prev.then(() => send(line)),
      Promise.resolve()
    )
  }, [
    authToken,
    conversationId,
    pendingEchoLines,
    drainEchoLines,
    suppressBufferedFlush,
    send,
  ])

  return echo
}
